import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { Worker, WorkerInput, WorkerUpdateInput } from '@jobos/shared/types/worker';
import { useToast } from '@/hooks/use-toast';

interface WorkersResponse {
  workers: Worker[];
}

interface WorkerDetailResponse {
  worker: Worker;
}

// Query keys
export const workerKeys = {
  all: ['workers'] as const,
  lists: () => [...workerKeys.all, 'list'] as const,
  list: (params?: { showInactive?: boolean }) =>
    [...workerKeys.lists(), params] as const,
  details: () => [...workerKeys.all, 'detail'] as const,
  detail: (id: string) => [...workerKeys.details(), id] as const,
};

// Fetch all workers with optional showInactive filter
export function useWorkers(showInactive = false) {
  return useQuery({
    queryKey: workerKeys.list({ showInactive }),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (showInactive) params.showInactive = 'true';

      const response = await apiClient.get<WorkersResponse>('/workers', {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
      return response.workers;
    },
  });
}

// Fetch single worker by ID
export function useWorker(id: string | undefined) {
  return useQuery({
    queryKey: workerKeys.detail(id!),
    queryFn: async () => {
      const response = await apiClient.get<WorkerDetailResponse>(`/workers/${id}`);
      return response.worker;
    },
    enabled: !!id,
  });
}

// Create worker mutation
export function useCreateWorker() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: WorkerInput) => {
      const response = await apiClient.post<WorkerDetailResponse>('/workers', data);
      return response.worker;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workerKeys.lists() });
      toast({
        title: 'Success',
        description: 'Worker created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create worker',
        variant: 'destructive',
      });
    },
  });
}

// Update worker mutation
export function useUpdateWorker(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: WorkerUpdateInput) => {
      const response = await apiClient.put<WorkerDetailResponse>(`/workers/${id}`, data);
      return response.worker;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workerKeys.lists() });
      toast({
        title: 'Success',
        description: 'Worker updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update worker',
        variant: 'destructive',
      });
    },
  });
}

// Deactivate worker mutation (soft delete)
export function useDeactivateWorker() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete<WorkerDetailResponse>(`/workers/${id}`);
      return response.worker;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workerKeys.lists() });
      toast({
        title: 'Success',
        description: 'Worker deactivated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to deactivate worker',
        variant: 'destructive',
      });
    },
  });
}

// Reactivate worker mutation
export function useReactivateWorker(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.put<WorkerDetailResponse>(`/workers/${id}`, {
        active: true,
      });
      return response.worker;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workerKeys.lists() });
      toast({
        title: 'Success',
        description: 'Worker reactivated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to reactivate worker',
        variant: 'destructive',
      });
    },
  });
}
